import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';

// Query keys for individual Executive KPIs
export const EXECUTIVE_KPI_KEYS = {
  TOTAL_REVENUE: 'executiveTotalRevenue',
  TOTAL_INVOICES: 'executiveTotalInvoices',
  CREDIT_USAGE_RATE: 'executiveCreditUsageRate',
  CREDIT_TO_REVENUE: 'executiveCreditToRevenue',
} as const;

// API service functions for individual KPIs
const apiService = {
  fetchTotalRevenue: async (branchId: number, timeRange?: string) => {
    const response = await apiClient.get(`/executive/${branchId}/kpis/total-revenue`, {
      params: timeRange
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch total revenue data');
    }
  },

  fetchTotalInvoices: async (branchId: number, timeRange?: string) => {
    const response = await apiClient.get(`/executive/${branchId}/kpis/total-invoices`, {
      params: timeRange
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch total invoices data');
    }
  },

  fetchCreditUsageRate: async (branchId: number, timeRange?: string) => {
    const response = await apiClient.get(`/executive/${branchId}/kpis/credit-usage-rate`, {
      params: timeRange
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch credit usage rate data');
    }
  },

  fetchCreditToRevenue: async (branchId: number, timeRange?: string) => {
    const response = await apiClient.get(`/executive/${branchId}/kpis/credit-to-revenue`, {
      params: timeRange
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch credit to revenue data');
    }
  },
};

// Hook for total revenue KPI
export function useExecutiveTotalRevenue(branchId?: number, timeRange?: string) {
  return useQuery({
    queryKey: [EXECUTIVE_KPI_KEYS.TOTAL_REVENUE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchTotalRevenue(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for total invoices KPI
export function useExecutiveTotalInvoices(branchId?: number, timeRange?: string) {
  return useQuery({
    queryKey: [EXECUTIVE_KPI_KEYS.TOTAL_INVOICES, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchTotalInvoices(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for credit usage rate KPI
export function useExecutiveCreditUsageRate(branchId?: number, timeRange?: string) {
  return useQuery({
    queryKey: [EXECUTIVE_KPI_KEYS.CREDIT_USAGE_RATE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchCreditUsageRate(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for credit to revenue KPI
export function useExecutiveCreditToRevenue(branchId?: number, timeRange?: string) {
  return useQuery({
    queryKey: [EXECUTIVE_KPI_KEYS.CREDIT_TO_REVENUE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchCreditToRevenue(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}